import { useEffect, useId, useMemo, useRef, useState } from "react";

import { userAPI } from "../UserAPI";
import Section from "../components/Section";
import Metric from "../components/Metric";
import { useTranslation } from "react-i18next";
import DIN276CategoryPicker from "../components/DIN276CategoryPicker";
import AssetDocumentsPanel from "../object-properties/AssetDocumentsPanel";
import ProductEligibilityPanel from "../object-properties/ProductEligibilityPanel";
import DinPropertySheet from "../object-properties/DinPropertySheet";
import DocumentUploadArea from "../components/DocumentUploadArea";
import {
    AndCondition,
    ComparisonCondition,
    Condition,
    Customer,
    EntityQuery,
    from,
    is,
    LegalPerson,
    ManagedFile,
    OrCondition,
    Product,
    Query
} from "@the-real-insight/tri-model";
import { authenticatedFetch } from "../JWTAuthService";
import { configuration } from "../Configuration";
import AssetList from "../components/AssetList";
import ConditionSection from "../components/ConditionSection";
import { useAdministrationAPI } from "../AdministrationAPI";
import { useModalContext } from "../components/ModalProvider";
import { useAtom } from "jotai";
import { useSearchParams } from "react-router-dom";
import { authenticationAtom } from "../State";
import { resolveDIN276CategoryCode } from "../utilities/din276Category";
import {
  legalPersonCompanyName,
} from "../utilities/legalPersonDisplay";
import { useUrlPageState } from "../utilities/dataTablePagination";
import { useGuideAction } from "../guide/useGuideAction";
import ReactApexChart from "react-apexcharts";
import { useCustomerTheme } from "../Theme";

import type { ColumnDef } from "@tanstack/react-table";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import {
  ChevronLeft,
  ChevronRight,
  Info,
  Loader2,
  Lock,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";
import { documentIdsOf } from "../utilities/documentReferences";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import InstalledBasePanel from "./InstalledBasePanel";
import { Textarea } from "@/components/ui/textarea";

interface Properties {}

// Universal dimensional fields surfaced for products regardless of category.
// Names double as storage keys in properties.* (same convention as DIN 276 fields).
const PRODUCT_DIMENSION_FIELDS = [
  { name: "Länge", type: "Ganzzahl", unit: "mm", enum: [], measured: true, typeUnclear: false },
  { name: "Breite", type: "Ganzzahl", unit: "mm", enum: [], measured: true, typeUnclear: false },
  { name: "Tiefe", type: "Ganzzahl", unit: "mm", enum: [], measured: true, typeUnclear: false },
];

/** Auswählbare Seitengrößen der Produktliste (Bestand). */
const PER_PAGE_OPTIONS = [10, 25, 50, 100];

enum FieldType {
  integerNumber = "integerNumber",
  decimalNumber = "decimalNumber",
  string = "string",
  boolean = "boolean",
}

class TelemetryField {
  name?: string;
  type: FieldType;

  constructor(
    name: string | undefined = undefined,
    type: FieldType = FieldType.integerNumber
  ) {
    this.name = name;
    this.type = type;
  }
}

/** Leerzustand einer Tabelle: Überschrift, Erklärung, Handlungsaufforderung. */
function TableEmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-4 text-center">
      <div className="text-sm font-semibold text-foreground">{title}</div>
      {hint ? (
        <div className="max-w-prose text-sm text-muted-foreground">{hint}</div>
      ) : null}
      {action}
    </div>
  );
}

/**
 * Betriebszustands-Legende: Farbfeld, Kurzbeschreibung und — bei Warnung und
 * Kritisch — der Bedingungsbaum.
 */
function OperatingStateRow({
  swatchClassName,
  description,
  children,
}: {
  swatchClassName: string;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-wrap items-start gap-4">
      <div
        aria-hidden
        className={`mt-0.5 h-8 w-8 shrink-0 rounded ${swatchClassName}`}
      />
      <div className="min-w-[200px] flex-1 break-words text-sm">{description}</div>
      <div className="min-w-0 flex-1 basis-full text-sm md:basis-[320px]">
        {children}
      </div>
    </div>
  );
}

export default function ProductManagementPanel(_properties: Properties) {
  const { t } = useTranslation();
  const { theme } = useCustomerTheme();

  const fieldType = [
    {
      key: FieldType.integerNumber,
      text: t("settingsPanel.productManagementPanel.integer"),
    },
    {
      key: FieldType.decimalNumber,
      text: t("settingsPanel.productManagementPanel.decimalNumber"),
    },
    {
      key: FieldType.string,
      text: t("settingsPanel.productManagementPanel.string"),
    },
    {
      key: FieldType.boolean,
      text: t("settingsPanel.productManagementPanel.boolean"),
    },
  ];
  const { pushError } = useModalContext() as any;
  const [authentication] = useAtom(authenticationAtom) as any;
  const { createOrUpdateProduct, uploadProductDocuments } = useAdministrationAPI();
  const [, setSearchParams] = useSearchParams();
  const [section, setSection] = useState<string>(() =>
    new URL(window.location.href).searchParams.get("section") === "installation" ? "installation" : "products",
  );
  const [tab, setTab] = useState("productData") as any;
  const [products, setProducts] = useState([]) as any;
  const [productsLoading, setProductsLoading] = useState(true);
  // The list holds lightweight stubs; the full product is loaded on open.
  const [productDetailLoading, setProductDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Guards against a stale full-product fetch overwriting a newer selection.
  const selectRequestRef = useRef<string | null>(null);
  // True while a ?product=<id> deep link is still being resolved on first load —
  // lets us show a detail-loading placeholder instead of flashing the list.
  const [deepLinkPending, setDeepLinkPending] = useState(
    () => !!new URL(window.location.href).searchParams.get("product"),
  );
  // Server-side pagination + aggregation: the panel never holds all 60k+ products.
  // `products` is the current page; the charts come from `distributions`.
  const [totalRows, setTotalRows] = useState(0);
  // Die Seite lebt in der Adresse (`?productPage=`), damit sie Reload und
  // Zurück-Taste übersteht — wie in den übrigen migrierten Tabellen.
  const { page: rawPage, onPageChange } = useUrlPageState("productPage");
  const page = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;
  const [perPage, setPerPage] = useState(10);
  const [distributions, setDistributions] = useState<{ byCategory: any[]; byVendor: any[] }>({
    byCategory: [],
    byVendor: [],
  });
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [telemetryFields, setTelemetryFields] = useState([]) as any;
  const [selectedProduct, setSelectedProduct] = useState() as any;
  const [nameTouched, setNameTouched] = useState(false);
  const [warningConditions, setWarningConditions] = useState([]) as any;
  const [criticalConditions, setCriticalConditions] = useState([]) as any;
  const [productDocs, setProductDocs] = useState([]) as any;
  const [bufferedDocs, setBufferedDocs] = useState<{ file: File; role: string }[]>([]);
  const [productFilter, setProductFilter] = useState("");
  const [onlyOwn, setOnlyOwn] = useState(false);
  const [withDocuments, setWithDocuments] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  // The raw schemaCategory values behind the active category filter (the server
  // matches these directly, since it can't resolve DIN 276 names → codes).
  const [categoryValuesFilter, setCategoryValuesFilter] = useState<string[] | undefined>(undefined);
  const [din276Metadata, setDin276Metadata] = useState<any[]>([]);
  // Same contract as `manufacturersLoaded`: settled, not "resolved". Codes that
  // carry no metadata (e.g. 474) stay rendered bare once loading finishes.
  const [din276Loaded, setDin276Loaded] = useState(false);
  const PRODUCT_DOC_ROLES = ["manual", "datasheet", "certificate", "approval", "drawing", "other"];
  const [tenantLegalPerson, setTenantLegalPerson] = useState<any>(undefined);
  // Settled, nicht „gefunden“: erst danach darf der Hinweis erscheinen, dass
  // dem Konto kein Hersteller-Unternehmen hinterlegt ist.
  const [tenantLoaded, setTenantLoaded] = useState(false);

  const filterFieldId = useId();
  const categoryFilterLabelId = useId();
  const onlyOwnId = useId();
  const withDocumentsId = useId();
  const perPageId = useId();
  const nameFieldId = useId();
  const summaryFieldId = useId();
  const schemaCategoryLabelId = useId();
  const telemetryFieldIdBase = useId();
  const bufferedDocIdBase = useId();

  // Filters sent to the server. `onlyOwn` is expressed as vendor = the tenant's
  // own LegalPerson id (mirrors the previous client-side manufacturer filter);
  // when there's no tenant LegalPerson yet, a sentinel matches nothing.
  const serverFilters = useMemo(
    () => {
      const ownVendor = onlyOwn
        ? tenantLegalPerson?._id
          ? String(tenantLegalPerson._id)
          : "__none__"
        : undefined;
      return {
        search: debouncedFilter || undefined,
        categoryValues:
          categoryValuesFilter && categoryValuesFilter.length > 0
            ? categoryValuesFilter
            : undefined,
        // Manufacturer filtering now happens via the search field (the vendor
        // donut populates it); `vendor` is only used for the "own products" toggle.
        vendor: ownVendor,
        withDocuments: withDocuments || undefined,
      };
    },
    [debouncedFilter, categoryValuesFilter, onlyOwn, withDocuments, tenantLegalPerson],
  );

  const filtersActive =
    productFilter.trim().length > 0 ||
    !!categoryFilter ||
    onlyOwn ||
    withDocuments;

  const resetFilters = () => {
    setProductFilter("");
    setCategoryFilter(undefined);
    setCategoryValuesFilter(undefined);
    setOnlyOwn(false);
    setWithDocuments(false);
  };

  // Count the filtered products per major DIN 276 category (first 3 digits of
  // the resolved cost-group code), grouping products without a category into a
  // dedicated "keine" bucket. Only the 15 largest categories are shown
  // individually; the remainder is aggregated into an "Andere" slice. Drives
  // the distribution pie chart above the table.
  const NO_CATEGORY_KEY = "__none__";
  const TOP_CATEGORIES = 15;
  // Neutral stand-in for a value whose lookup data is still in flight.
  const categoryDistribution = useMemo(() => {
    const counts = new Map<string, number>();
    // The raw `schemaCategory` values (names/codes) that resolve to each major
    // key — passed to the server as the filter when a slice is clicked, since the
    // server can't resolve category names itself.
    const valuesByKey = new Map<string, string[]>();
    for (const bucket of distributions.byCategory) {
      const code = resolveDIN276CategoryCode(bucket?.schemaCategory, din276Metadata);
      const major = String(code || "").replace(/\D/g, "").slice(0, 3);
      const key = major || NO_CATEGORY_KEY;
      counts.set(key, (counts.get(key) || 0) + (Number(bucket?.count) || 0));
      const raw = String(bucket?.schemaCategory ?? "");
      if (raw) {
        const arr = valuesByKey.get(key) || [];
        arr.push(raw);
        valuesByKey.set(key, arr);
      }
    }

    const nameByCode = new Map<string, string>();
    for (const m of din276Metadata) {
      const c = String(m?.code ?? "").trim();
      if (c) nameByCode.set(c, String(m?.name ?? ""));
    }

    const labelFor = (key: string) => {
      if (key === NO_CATEGORY_KEY)
        return t("settingsPanel.productManagementPanel.noCategory");
      const name = nameByCode.get(key);
      return name ? `${key} — ${name}` : key;
    };

    // Rank by count (largest first); ties broken by code for stable ordering.
    const ranked = Array.from(counts.entries()).sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      if (a[0] === NO_CATEGORY_KEY) return 1;
      if (b[0] === NO_CATEGORY_KEY) return -1;
      return parseInt(a[0], 10) - parseInt(b[0], 10);
    });

    const top = ranked.slice(0, TOP_CATEGORIES);
    const rest = ranked.slice(TOP_CATEGORIES);

    // The 15 largest are *selected* by count above; named categories are then
    // *displayed* alphabetically by legend label, followed by "Andere", with the
    // grey "Keine" bucket always last.
    const named = top
      .filter(([key]) => key !== NO_CATEGORY_KEY)
      .sort((a, b) => labelFor(a[0]).localeCompare(labelFor(b[0])));
    const none = top.find(([key]) => key === NO_CATEGORY_KEY);

    const labels = named.map(([key]) => labelFor(key));
    const series = named.map(([, count]) => count);
    // Parallel array of the 3-digit category code per slice (null for the
    // aggregate "Andere" / "Keine" buckets) so a click can set the filter.
    const codes: (string | null)[] = named.map(([key]) => key);
    const categoryValues: (string[] | null)[] = named.map(
      ([key]) => valuesByKey.get(key) || [],
    );

    if (rest.length > 0) {
      labels.push(t("settingsPanel.productManagementPanel.otherCategories"));
      series.push(rest.reduce((sum, [, count]) => sum + count, 0));
      codes.push(null);
      categoryValues.push(null);
    }
    if (none) {
      labels.push(labelFor(none[0]));
      series.push(none[1]);
      codes.push(null);
      categoryValues.push(null);
    }

    return { labels, series, codes, categoryValues };
  }, [distributions, din276Metadata, t]);

  // Vendor distribution from the server aggregation, grouped by manufacturer id;
  // the legend label is "<company name>, <city>".
  const vendorDistribution = useMemo(() => {
    const counts = new Map<string, number>();
    const labelByKey = new Map<string, string>();
    const nameByKey = new Map<string, string>();
    for (const bucket of distributions.byVendor) {
      const id = bucket?.manufacturer ? String(bucket.manufacturer) : null;
      const name = String(bucket?.name || "").trim();
      const key = id && name ? id : NO_CATEGORY_KEY;
      counts.set(key, (counts.get(key) || 0) + (Number(bucket?.count) || 0));
      if (key !== NO_CATEGORY_KEY && !labelByKey.has(key)) {
        const city = String(bucket?.city || "").trim();
        labelByKey.set(key, city ? `${name}, ${city}` : name);
        nameByKey.set(key, name);
      }
    }

    const labelFor = (key: string) =>
      key === NO_CATEGORY_KEY
        ? t("settingsPanel.productManagementPanel.noVendor")
        : labelByKey.get(key) || key;

    // Rank by count (largest first); ties broken by label for stable ordering.
    const ranked = Array.from(counts.entries()).sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      if (a[0] === NO_CATEGORY_KEY) return 1;
      if (b[0] === NO_CATEGORY_KEY) return -1;
      return labelFor(a[0]).localeCompare(labelFor(b[0]));
    });

    const top = ranked.slice(0, TOP_CATEGORIES);
    const rest = ranked.slice(TOP_CATEGORIES);

    const named = top
      .filter(([key]) => key !== NO_CATEGORY_KEY)
      .sort((a, b) => labelFor(a[0]).localeCompare(labelFor(b[0])));
    const none = top.find(([key]) => key === NO_CATEGORY_KEY);

    const labels = named.map(([key]) => labelFor(key));
    const series = named.map(([, count]) => count);
    // Parallel arrays of the manufacturer id and plain company name per slice
    // (null for aggregate buckets) so a click can populate the search field.
    const manufacturerIds: (string | null)[] = named.map(([key]) => key);
    const names: (string | null)[] = named.map(([key]) => nameByKey.get(key) || null);

    if (rest.length > 0) {
      labels.push(t("settingsPanel.productManagementPanel.otherCategories"));
      series.push(rest.reduce((sum, [, count]) => sum + count, 0));
      manufacturerIds.push(null);
      names.push(null);
    }
    if (none) {
      labels.push(labelFor(none[0]));
      series.push(none[1]);
      manufacturerIds.push(null);
      names.push(null);
    }

    return { labels, series, manufacturerIds, names };
  }, [distributions, t]);

  // Headline metrics for the filtered set (from the server aggregation).
  const productMetrics = useMemo(() => {
    return {
      total: totalRows,
      vendors: distributions.byVendor.filter((v: any) => v?.manufacturer).length,
      avgDocs: Number((distributions as any).avgDocs) || 0,
    };
  }, [totalRows, distributions]);

  const pieChartOptions = useMemo(() => {
    // NB: do NOT feed theme.* colors here — those resolve to CSS-variable /
    // rgb() strings, and ApexCharts' hex parser calls `.match(...).length`,
    // crashing ("reading 'length' of null") on any non-hex color. Use a fixed
    // hex palette with enough entries for the 10 + "Andere" slices.
    const PALETTE = [
      "#001A61",
      "#5F3A79",
      "#98AC4D",
      "#2F65F9",
      "#D98F00",
      "#0E8A6B",
      "#B5536E",
      "#3E7CB1",
      "#C44E00",
      "#7A7A52",
      "#9A4C95",
      "#1F6F6F",
      "#A63A2A",
      "#4B5D2A",
      "#7E5A9B",
      "#2C7BB6",
    ];
    const GREY = "#9E9E9E";
    // The catch-all buckets ("Keine" / "Unbekannt" / "Andere") are always
    // rendered grey; the remaining slices cycle through the palette so no
    // palette color is skipped.
    const noneLabels = new Set([
      t("settingsPanel.productManagementPanel.noCategory"),
      t("settingsPanel.productManagementPanel.noVendor"),
      t("settingsPanel.productManagementPanel.otherCategories"),
    ]);
    return (labels: string[], onSelect?: (index: number) => void) => {
      let p = 0;
      return {
        labels,
        chart: {
          events: onSelect
            ? {
                dataPointSelection: (_e: any, _ctx: any, cfg: any) =>
                  onSelect(cfg?.dataPointIndex),
              }
            : {},
        },
        states: { active: { filter: { type: "none" as const } } },
        // Fixed legend width so both charts reserve identical horizontal space
        // for the legend — otherwise the wider (vendor) legend shrinks its pie
        // relative to the category pie.
        legend: { show: true, position: "right" as const, width: 350 },
        // Scale the donut graphic down within the plot area (keeps the box and
        // legend size unchanged).
        plotOptions: { pie: { customScale: 0.9 } },
        colors: labels.map((label) =>
          noneLabels.has(label) ? GREY : PALETTE[p++ % PALETTE.length],
        ),
        tooltip: {
          y: {
            formatter: (val: number) =>
              `${val} ${t("settingsPanel.productManagementPanel.productsUnit")}`,
          },
        },
      };
    };
  }, [t]);

  // Both donuts group and rank by the *resolved* label, so before their lookup
  // data lands they don't just show unlabelled slices — they show a different
  // distribution that is recomputed from scratch once it does. Holding the
  // chart back until then keeps the fixed 248px box (no reflow) and lets the
  // legend paint once, with its final labels.
  const donutPending = <Skeleton className="h-full w-full" />;

  const categoryChartOptions = useMemo(
    () =>
      pieChartOptions(categoryDistribution.labels, (i: number) => {
        const code = categoryDistribution.codes[i];
        const values = categoryDistribution.categoryValues[i];
        if (!code) return;
        const isActive = categoryFilter === code;
        setCategoryFilter(isActive ? undefined : code);
        setCategoryValuesFilter(
          isActive || !values || values.length === 0 ? undefined : values,
        );
      }),
    [pieChartOptions, categoryDistribution, categoryFilter],
  );
  const vendorChartOptions = useMemo(
    () =>
      pieChartOptions(vendorDistribution.labels, (i: number) => {
        const name = vendorDistribution.names[i];
        if (name) {
          // Populate the search field with the manufacturer name so the filter is
          // visible and can simply be cleared to reset.
          setProductFilter(name);
        }
      }),
    [pieChartOptions, vendorDistribution],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const metadata: any[] = await userAPI.getDIN276Metadata();
        if (!cancelled && Array.isArray(metadata)) {
          setDin276Metadata(metadata);
        }
      } catch {
        /* category-name filtering is best-effort; code filters still work */
      } finally {
        // Settle on failure too — bare codes are the honest fallback then.
        if (!cancelled) setDin276Loaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!authentication?.customer) return;
        const customer: any = await userAPI.queryOne(
          from(Customer.type).where(is("id").equal(authentication.customer)),
        );
        if (!customer?.legalPerson) return;
        const lp: any = await userAPI.queryOne(
          from(LegalPerson.type).where(is("_id").equal(customer.legalPerson)),
        );
        if (!cancelled && lp) setTenantLegalPerson(lp);
      } catch (error) {
        pushError(error);
      } finally {
        if (!cancelled) setTenantLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer]);

  // Path our crawler writes: /Product/<Mfg> - <City>/<Model>/<role>/<filename>
  // → title = last segment; role comes from ManagedFile.roles[0] when set, else the
  // second-to-last path segment as a fallback.
  const docSegments = (path: string = "") => path.split("/").filter(Boolean);
  const docTitle = (path: string) => {
    const segment = docSegments(path).pop() || "";
    try {
      return decodeURIComponent(segment);
    } catch {
      return segment;
    }
  };
  const ROLE_KEYS: Record<string, string> = {
    datasheet: "roleDatasheet",
    manual: "roleManual",
    certificate: "roleCertificate",
    approval: "roleApproval",
    drawing: "roleDrawing",
    other: "roleOther",
  };
  const docRole = (doc: any) => {
    const fromRoles = Array.isArray(doc?.roles) && doc.roles.length > 0 ? doc.roles[0] : "";
    const fromPath = docSegments(doc?.path || "").slice(-2)[0] || "other";
    return String(fromRoles || fromPath).toLowerCase();
  };
  const roleLabel = (doc: any) =>
    t(
      `settingsPanel.productManagementPanel.${
        ROLE_KEYS[docRole(doc)] || "roleOther"
      }`
    );

  // Product documents are public (shared=true). Fetch by ManagedFile _id so we cross
  // tenant boundaries: tri-server resolves the file from its owning customer's storage
  // regardless of the logged-in user's customer.
  const openDocument = async (doc: any) => {
    try {
      const id = doc?._id ? String(doc._id) : "";
      if (!id) return;
      const response = await authenticatedFetch(
        `${configuration.serverUrl}/managed-files/${id}`,
        { method: "GET", headers: { Accept: "*/*" } }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch document (${response.status})`);
      }
      const blob = await response.blob();
      const contentType =
        response.headers.get("content-type") || "application/octet-stream";
      const url = URL.createObjectURL(new Blob([blob], { type: contentType }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      pushError(error);
    }
  };

  // Fixed-width cell: truncate long content with an ellipsis and expose the full
  // value as a native tooltip (title) so nothing is lost when truncated.
  const cellText = (text: any) => {
    const s = text == null || text === "" ? "-" : String(text);
    return (
      <span title={s} className="block w-full truncate">
        {s}
      </span>
    );
  };

  // The list already carries the manufacturer name (server-joined).
  const manufacturerLabel = (product: any) => product?.manufacturerName || "-";

  // Encode the selected product id in the URL so it survives reloads and can be
  // deep-linked. Läuft über den Router, damit `?tab=` und `?productPage=`
  // (ebenfalls Router-geschrieben) nicht gegenseitig verloren gehen.
  const setProductParam = (id: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) {
        next.set("product", id);
      } else {
        next.delete("product");
      }
      return next;
    });
  };

  const applyProduct = (product: any) => {
    setSelectedProduct(product);
    setNameTouched(false);
    setWarningConditions(product?.warningConditions || []);
    setCriticalConditions(product?.criticalConditions || []);
    setTelemetryFields(product?.telemetry?.fields || []);
  };

  const selectProduct = async (product: any) => {
    if (!product) {
      selectRequestRef.current = null;
      setSelectedProduct(null);
      setProductParam(null);
      return;
    }

    const id = product?._id ? String(product._id) : null;
    selectRequestRef.current = id;
    setProductParam(id);

    // Show the stub immediately (name/category/manufacturer), then hydrate with
    // the full product — the list only carries lightweight stubs, so telemetry,
    // conditions, properties and documents are fetched on open.
    applyProduct(product);

    if (id) {
      setProductDetailLoading(true);
      try {
        const full: any = await userAPI.getEntityByID("Product", id);
        // Ignore if the user has since selected a different product.
        if (full && selectRequestRef.current === id) {
          applyProduct(full);
        }
      } catch (error) {
        // Keep the stub on failure — the detail view shows what we have.
      } finally {
        if (selectRequestRef.current === id) {
          setProductDetailLoading(false);
        }
      }
    }
  };

  const closeProduct = () => {
    selectRequestRef.current = null;
    setBufferedDocs([]);
    setSelectedProduct(null);
    setProductParam(null);
  };

  // Debounce the free-text filter before it hits the server.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedFilter(productFilter.trim()), 300);
    return () => clearTimeout(handle);
  }, [productFilter]);

  const filtersKey = JSON.stringify(serverFilters);

  // Any filter change returns to the first page. Der erste Lauf wird
  // übersprungen, damit ein Direkteinstieg per ?productPage=… nicht sofort
  // auf Seite 1 zurückgesetzt wird.
  const filtersSettled = useRef(false);
  useEffect(() => {
    if (!filtersSettled.current) {
      filtersSettled.current = true;
      return;
    }
    onPageChange(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  // Clamp the current page when totalRows decreases for the same filters (server-side
  // pagination). Without this, deletions or total corrections can leave us on page 77
  // while only 3 pages exist, showing an empty table.
  useEffect(() => {
    if (totalRows === 0) return;
    const maxPage = Math.max(1, Math.ceil(totalRows / perPage));
    if (page > maxPage) {
      onPageChange(maxPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalRows, perPage, page]);

  // Load the current page of products (server-side pagination + search).
  useEffect(() => {
    let cancelled = false;
    setProductsLoading(true);
    (async () => {
      try {
        const { rows, total } = await userAPI.getProductPage({
          ...serverFilters,
          skip: (page - 1) * perPage,
          limit: perPage,
        });
        if (!cancelled) {
          setProducts(rows);
          setTotalRows(total);
        }
      } catch (error) {
        if (!cancelled) pushError(error);
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, page, perPage]);

  // Load the chart distributions (same filters, whole set) via server aggregation.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dist = await userAPI.getProductDistributions(serverFilters);
        if (!cancelled) setDistributions(dist as any);
      } catch (error) {
        // Charts are non-critical — leave them empty on error.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  // Restore a ?product=<id> deep link once, by loading that product directly.
  useEffect(() => {
    const deepLinkProductId = new URL(window.location.href).searchParams.get("product");
    if (deepLinkProductId) {
      void selectProduct({ _id: deepLinkProductId });
    }
    setDeepLinkPending(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the ManagedFile docs referenced by the selected product's `documents` array.
  // We issue one query per id so each goes through tri-server's _id-direct-lookup path
  // (which converts the string to ObjectId); an OR over multiple _ids doesn't, so
  // multi-document products would otherwise return nothing.
  useEffect(() => {
    const call = async () => {
      const ids = documentIdsOf(selectedProduct?.documents);
      if (ids.length === 0) {
        setProductDocs([]);
        return;
      }
      try {
        const results = await Promise.all(
          ids.map(async (id: string) => {
            const rows = await userAPI.query(
              new Query(new EntityQuery(ManagedFile.type)).where(
                (is("_id") as any).equal(id)
              )
            );
            return Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
          })
        );
        setProductDocs(results.filter(Boolean));
      } catch (error) {
        pushError(error);
        setProductDocs([]);
      }
    };
    call();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  const fieldsComplete = () => {
    for (const field of telemetryFields) {
      if (!field.name || field.name.trim().length === 0) {
        return false;
      }
    }

    return true;
  };

  const fieldsValid = () => {
    if (!selectedProduct?.name || String(selectedProduct.name).trim().length === 0) {
      return false;
    }

    if (telemetryFields.length === 0) {
      return true;
    }

    if (!fieldsComplete()) {
      return false;
    }

    for (const field of telemetryFields) {
      for (const otherField of telemetryFields) {
        if (field === otherField) {
          continue;
        }

        if (field.name === otherField.name) {
          return false;
        }
      }
    }

    return true;
  };

  // Menü „Bedingung ergänzen“ — Vergleich, UND- oder ODER-Verknüpfung. Bewusst
  // eine Render-Funktion und keine im Render definierte Komponente: letztere
  // bekäme bei jedem Elternrender einen neuen Typ und würde das offene Menü
  // ausmounten.
  const renderAddConditionMenu = (addCondition: (condition: Condition) => void) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Plus aria-hidden className="h-4 w-4" />
          {t("settingsPanel.productManagementPanel.addCondition")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onSelect={() => addCondition(new ComparisonCondition())}>
          {t("settingsPanel.productManagementPanel.comparison")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => addCondition(new AndCondition())}>
          {t("settingsPanel.productManagementPanel.andOperation")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => addCondition(new OrCondition())}>
          {t("settingsPanel.productManagementPanel.orOperation")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const changeTab = (key: string) => {
    setTab(key);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", key);
      return next;
    });
  };

  // A saved product is editable only when it belongs to the current tenant.
  // Global catalog products (_tenant null) and other tenants' products are
  // read-only; a new (unsaved) product is always editable (it becomes the
  // tenant's own on save).
  const productReadOnly =
    !!selectedProduct?._id &&
    selectedProduct?._tenant != null &&
    String(selectedProduct._tenant) !== String(authentication?.customer ?? "");

  const saveProduct = async () => {
    if (productReadOnly) {
      return;
    }
    setSaving(true);
    try {
      const newProduct = new Product({
        ...selectedProduct,
        telemetry: {
          fields: telemetryFields,
        },
        warningConditions,
        criticalConditions,
      });

      const saved: any = await createOrUpdateProduct(newProduct);

      let uploadFailed = false;
      if (bufferedDocs.length > 0 && saved?._id) {
        try {
          await uploadProductDocuments(String(saved._id), bufferedDocs);
        } catch (uploadError) {
          uploadFailed = true;
          pushError(
            new Error(
              t("settingsPanel.productManagementPanel.documentUploadFailed", {
                error:
                  uploadError instanceof Error
                    ? uploadError.message
                    : String(uploadError),
              })
            )
          );
        }
      }

      // Refresh the current page + charts from the server.
      try {
        const [{ rows, total }, dist] = await Promise.all([
          userAPI.getProductPage({
            ...serverFilters,
            skip: (page - 1) * perPage,
            limit: perPage,
          }),
          userAPI.getProductDistributions(serverFilters),
        ]);
        setProducts(rows);
        setTotalRows(total);
        setDistributions(dist as any);
      } catch {
        // Non-fatal: the list refresh can fail without losing the save.
      }

      // Der Fehler des Upload-Zweigs steht bereits als eigene Meldung — dann
      // wäre eine zusätzliche Erfolgsmeldung widersprüchlich.
      if (!uploadFailed) {
        toast.success(t("settingsPanel.productManagementPanel.productSaved"));
      }

      selectRequestRef.current = null;
      setBufferedDocs([]);
      setSelectedProduct(null);
      setProductParam(null);
    } catch (error) {
      pushError(error);
    } finally {
      setSaving(false);
    }
  };

  // ── Produktliste ────────────────────────────────────────────────────────

  const columns: ColumnDef<any>[] = [
    {
      id: "name",
      header: () => t("settingsPanel.productManagementPanel.product"),
      accessorFn: (product: any) => product?.name,
      cell: ({ row }) => {
        const value = row.original?.name;
        const label = value == null || value === "" ? "-" : String(value);
        return (
          <button
            type="button"
            title={label}
            className="block w-full truncate rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={(event) => {
              event.stopPropagation();
              void selectProduct(row.original);
            }}
          >
            {label}
          </button>
        );
      },
    },
    {
      id: "din276Type",
      header: () => t("settingsPanel.productManagementPanel.din276Type"),
      accessorFn: (product: any) =>
        resolveDIN276CategoryCode(product?.schemaCategory, din276Metadata),
      cell: ({ row }) =>
        cellText(
          resolveDIN276CategoryCode(row.original?.schemaCategory, din276Metadata),
        ),
    },
    {
      id: "category",
      header: () => t("settingsPanel.productManagementPanel.category"),
      accessorFn: (product: any) => product?.schemaCategory,
      cell: ({ row }) => cellText(row.original?.schemaCategory),
    },
    {
      id: "manufacturer",
      header: () => t("settingsPanel.productManagementPanel.manufacturer"),
      accessorFn: (product: any) => manufacturerLabel(product),
      cell: ({ row }) => cellText(manufacturerLabel(row.original)),
    },
  ];

  const COLUMN_WIDTHS: Record<string, string> = {
    name: "w-[300px]",
    din276Type: "w-[130px]",
    category: "w-[150px]",
    manufacturer: "w-[150px]",
  };

  const table = useReactTable({
    data: products,
    columns,
    state: { pagination: { pageIndex: Math.max(0, page - 1), pageSize: perPage } },
    manualPagination: true,
    rowCount: totalRows,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = Math.max(1, Math.ceil(totalRows / perPage));
  const rangeFrom = totalRows === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeTo = Math.min(page * perPage, totalRows);

  /**
   * Prefill from the URL
   * (`?panel=productManagement&guide=settings.productManagement.create&name=…&productSummary=…`):
   * the same as the „Produkt erstellen" button, just with name and short description taken from
   * the URL. Telemetry fields and thresholds are separate lists and cannot be set via the URL.
   *
   * `ready` tied to `tenantLegalPerson`: the button is disabled without a stored company (it is
   * entered as the manufacturer), and the hook bypasses the button. While the company is still
   * loading, it waits; if none is stored, it opens nothing and the existing hint
   * (`createProductNeedsCompany`) stays visible. This is `guideAction.requiresState`
   * = `tenantLegalPerson` of the registry entry.
   */
  useGuideAction(
    "settings.productManagement.create",
    (values) => {
      setWarningConditions([]);
      setCriticalConditions([]);
      setTelemetryFields([]);
      setBufferedDocs([]);
      setNameTouched(false);
      setSelectedProduct({
        name: values.name ?? "",
        productSummary: values.productSummary,
        schema: "DIN276",
        manufacturer: tenantLegalPerson,
      });
    },
    { panel: "productManagement", ready: Boolean(tenantLegalPerson) },
  );

  const productList = (
    <>
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-4">
        <h2 className="min-w-0 break-words text-lg font-semibold">
          {t("settingsPanel.productManagementPanel.products")}
        </h2>
        <Button
          data-guide-id="settings.productManagement.create"
          className="gap-2 max-sm:w-full"
          disabled={!tenantLegalPerson}
          title={
            tenantLegalPerson
              ? undefined
              : t("settingsPanel.productManagementPanel.createProductNeedsCompany")
          }
          onClick={() => {
            setWarningConditions([]);
            setCriticalConditions([]);
            setTelemetryFields([]);
            setBufferedDocs([]);
            setNameTouched(false);
            setSelectedProduct({
              name: "",
              schema: "DIN276",
              manufacturer: tenantLegalPerson,
            });
          }}
        >
          <Plus aria-hidden className="h-4 w-4" />
          {t("settingsPanel.productManagementPanel.createProduct")}
        </Button>
      </div>
      {tenantLoaded && !tenantLegalPerson ? (
        <Alert variant="info">
          <Info aria-hidden className="h-4 w-4" />
          <AlertDescription>
            {t("settingsPanel.productManagementPanel.createProductNeedsCompany")}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex min-w-0 flex-wrap items-end gap-4">
        <div className="flex min-w-[220px] flex-1 flex-col gap-1.5 sm:max-w-[320px]">
          <Label htmlFor={filterFieldId}>
            {t("settingsPanel.productManagementPanel.productFilterLabel")}
          </Label>
          <div className="relative min-w-0">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id={filterFieldId}
              type="search"
              className="px-9"
              value={productFilter}
              placeholder={t(
                "settingsPanel.productManagementPanel.productFilterPlaceholder",
              )}
              onChange={(event) => setProductFilter(event.target.value)}
            />
            {productFilter ? (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-9 w-9 max-sm:h-9 max-sm:w-9"
                aria-label={t("settingsPanel.productManagementPanel.clearFilter")}
                title={t("settingsPanel.productManagementPanel.clearFilter")}
                onClick={() => setProductFilter("")}
              >
                <X aria-hidden className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>
        <div
          role="group"
          aria-labelledby={categoryFilterLabelId}
          className="flex min-w-[220px] flex-1 flex-col gap-1.5 sm:max-w-[360px]"
        >
          <span
            id={categoryFilterLabelId}
            className="text-sm font-medium leading-none"
          >
            {t("settingsPanel.productManagementPanel.categoryFilterLabel")}
          </span>
          <DIN276CategoryPicker
            className="!w-full [&>button]:!w-full"
            value={categoryFilter}
            onChange={(code) => {
              setCategoryFilter(code);
              if (!code) {
                setCategoryValuesFilter(undefined);
                return;
              }
              // Resolve the picked code to the raw schemaCategory values in
              // the current category buckets (the server matches these).
              const isMajor = /^\d{3}$/.test(code);
              const values = (distributions.byCategory || [])
                .map((b: any) => String(b?.schemaCategory ?? ""))
                .filter((raw: string) => {
                  if (!raw) return false;
                  const c = resolveDIN276CategoryCode(raw, din276Metadata);
                  return isMajor
                    ? String(c).replace(/\D/g, "").slice(0, 3) === code
                    : c === code;
                });
              setCategoryValuesFilter(values.length > 0 ? values : undefined);
            }}
            includeEmptyOption
            emptyOptionText={t(
              "settingsPanel.productManagementPanel.allCategories",
            )}
            placeholder={t(
              "settingsPanel.productManagementPanel.schemaCategoryFilterPlaceholder",
            )}
          />
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-4 py-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id={onlyOwnId}
              checked={onlyOwn}
              disabled={!tenantLegalPerson}
              onCheckedChange={(checked) => setOnlyOwn(checked === true)}
            />
            <Label htmlFor={onlyOwnId} className="font-normal">
              {t("settingsPanel.productManagementPanel.onlyOwnProducts")}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={withDocumentsId}
              checked={withDocuments}
              onCheckedChange={(checked) => setWithDocuments(checked === true)}
            />
            <Label htmlFor={withDocumentsId} className="font-normal">
              {t("settingsPanel.productManagementPanel.withDocuments")}
            </Label>
          </div>
        </div>
      </div>

      {totalRows > 0 && (
        <div className="flex min-w-0 flex-wrap gap-6">
          <Metric
            size="large"
            title={t("settingsPanel.productManagementPanel.totalProducts")}
            value={productMetrics.total.toLocaleString()}
            color={theme.secondary}
          />
          <Metric
            size="large"
            title={t("settingsPanel.productManagementPanel.totalVendors")}
            value={productMetrics.vendors.toLocaleString()}
            color={theme.tertiary}
          />
          <Metric
            size="large"
            title={t(
              "settingsPanel.productManagementPanel.avgDocumentsPerProduct",
            )}
            value={productMetrics.avgDocs.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            color={theme.primary}
          />
        </div>
      )}

      {categoryDistribution.series.length > 0 && (
        <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
          <Section
            className="min-w-0"
            title={t(
              "settingsPanel.productManagementPanel.categoryDistribution",
            )}
          >
            <div className="h-[248px] min-w-0">
              {din276Loaded ? (
                <ReactApexChart
                  options={categoryChartOptions as any}
                  series={categoryDistribution.series}
                  type="donut"
                  height="100%"
                />
              ) : (
                donutPending
              )}
            </div>
          </Section>
          <Section
            className="min-w-0"
            title={t("settingsPanel.productManagementPanel.vendorDistribution")}
          >
            <div className="h-[248px] min-w-0">
              <ReactApexChart
                options={vendorChartOptions as any}
                series={vendorDistribution.series}
                type="donut"
                height="100%"
              />
            </div>
          </Section>
        </div>
      )}

      <div className="min-w-0 overflow-x-auto">
        <div className="rounded-md border">
          <Table className="min-w-[760px] table-fixed">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={COLUMN_WIDTHS[header.column.id]}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {productsLoading ? (
                Array.from({ length: 5 }).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {columns.map((_column, cellIndex) => (
                      <TableCell key={cellIndex}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() => void selectProduct(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {filtersActive ? (
                      <TableEmptyState
                        title={t(
                          "settingsPanel.productManagementPanel.noMatchingProductsTitle",
                        )}
                        hint={t(
                          "settingsPanel.productManagementPanel.noMatchingProductsHint",
                        )}
                        action={
                          <Button variant="outline" size="sm" onClick={resetFilters}>
                            {t("settingsPanel.productManagementPanel.resetFilters")}
                          </Button>
                        }
                      />
                    ) : (
                      <TableEmptyState
                        title={t(
                          "settingsPanel.productManagementPanel.noProductsTitle",
                        )}
                        hint={t(
                          "settingsPanel.productManagementPanel.noProductsHint",
                        )}
                        action={
                          tenantLegalPerson ? (
                            <Button
                              data-guide-id="settings.productManagement.create"
                              variant="outline"
                              size="sm"
                              className="gap-2"
                              onClick={() => {
                                setWarningConditions([]);
                                setCriticalConditions([]);
                                setTelemetryFields([]);
                                setBufferedDocs([]);
                                setNameTouched(false);
                                setSelectedProduct({
                                  name: "",
                                  schema: "DIN276",
                                  manufacturer: tenantLegalPerson,
                                });
                              }}
                            >
                              <Plus aria-hidden className="h-4 w-4" />
                              {t(
                                "settingsPanel.productManagementPanel.createProduct",
                              )}
                            </Button>
                          ) : undefined
                        }
                      />
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {!productsLoading && totalRows > 0 && (
            <div className="flex min-w-0 flex-wrap items-center justify-end gap-3 border-t px-4 py-2">
              <div className="mr-auto text-sm text-muted-foreground">
                {t("settingsPanel.productManagementPanel.paginationRange", {
                  from: rangeFrom.toLocaleString(),
                  to: rangeTo.toLocaleString(),
                  total: totalRows.toLocaleString(),
                })}
              </div>
              <div className="flex items-center gap-2">
                <Label
                  htmlFor={perPageId}
                  className="whitespace-nowrap font-normal text-muted-foreground"
                >
                  {t("settingsPanel.dataTable.rowsPerPageText")}
                </Label>
                <Select
                  value={String(perPage)}
                  onValueChange={(value) => {
                    setPerPage(Number(value));
                    onPageChange(1);
                  }}
                >
                  <SelectTrigger id={perPageId} className="w-[86px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PER_PAGE_OPTIONS.map((option) => (
                      <SelectItem key={option} value={String(option)}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={t("settingsPanel.dataTable.previousText")}
                  disabled={page <= 1}
                  onClick={() => onPageChange(Math.max(1, page - 1))}
                >
                  <ChevronLeft aria-hidden className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  {page} / {pageCount}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={t("settingsPanel.dataTable.nextText")}
                  disabled={page >= pageCount}
                  onClick={() => onPageChange(Math.min(pageCount, page + 1))}
                >
                  <ChevronRight aria-hidden className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  // ── Produktdetail ───────────────────────────────────────────────────────

  const nameValue = selectedProduct?.name ?? "";
  const nameInvalid = String(nameValue).trim().length === 0;

  const productDetail = selectedProduct ? (
    <>
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="min-w-0 break-words text-lg font-semibold">
            {selectedProduct.name ||
              t("settingsPanel.productManagementPanel.newProduct")}
          </h2>
          {productDetailLoading ? (
            <>
              <Loader2
                aria-hidden
                className="h-4 w-4 shrink-0 animate-spin text-muted-foreground"
              />
              <span className="sr-only">{t("global.loading")}</span>
            </>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2 max-sm:w-full">
          <Button
            variant="outline"
            className="max-sm:flex-1"
            onClick={closeProduct}
          >
            {t("settingsPanel.productManagementPanel.close")}
          </Button>
          <Button
            className="gap-2 max-sm:flex-1"
            disabled={
              !fieldsValid() || productReadOnly || productDetailLoading || saving
            }
            onClick={saveProduct}
          >
            {saving ? (
              <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
            ) : null}
            {saving
              ? t("settingsPanel.productManagementPanel.saving")
              : t("settingsPanel.productManagementPanel.save")}
          </Button>
        </div>
      </div>

      {productReadOnly ? (
        <Alert variant="warning">
          <Lock aria-hidden className="h-4 w-4" />
          <AlertDescription>
            {t("settingsPanel.productManagementPanel.readOnlyNotOwned")}
          </AlertDescription>
        </Alert>
      ) : null}

      <Tabs value={tab} onValueChange={changeTab} className="min-w-0">
        <TabsList className="max-w-full">
          <TabsTrigger value="productData">
            {t("settingsPanel.productManagementPanel.productData")}
          </TabsTrigger>
          {!productReadOnly ? (
            <TabsTrigger value="telemetry">
              {t("settingsPanel.productManagementPanel.telemetry")}
            </TabsTrigger>
          ) : null}
          {!productReadOnly ? (
            <TabsTrigger value="operatingState">
              {t("settingsPanel.productManagementPanel.operatingState")}
            </TabsTrigger>
          ) : null}
          <TabsTrigger value="devices">
            {t("settingsPanel.productManagementPanel.devices")}
          </TabsTrigger>
          {selectedProduct._id ? (
            <TabsTrigger value="eligibility">
              {t("settingsPanel.productManagementPanel.eligibility")}
            </TabsTrigger>
          ) : null}
          <TabsTrigger value="documents">
            {t("settingsPanel.productManagementPanel.documents")}
          </TabsTrigger>
        </TabsList>

        {/* Produktdaten */}
        <TabsContent value="productData">
          <div className="flex min-w-0 max-w-2xl flex-col gap-4">
            {!selectedProduct._id && tenantLegalPerson ? (
              <Alert variant="info">
                <Info aria-hidden className="h-4 w-4" />
                <AlertDescription>
                  {t(
                    "settingsPanel.productManagementPanel.assignedToTenantManufacturer",
                    { company: legalPersonCompanyName(tenantLegalPerson) },
                  )}
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="flex min-w-0 flex-col gap-1.5 sm:max-w-sm">
              <Label htmlFor={nameFieldId}>
                {t("settingsPanel.productManagementPanel.name")}
                <span aria-hidden className="ml-0.5 text-destructive">
                  *
                </span>
              </Label>
              <Input
                id={nameFieldId}
                required
                value={nameValue}
                disabled={productReadOnly}
                aria-invalid={nameTouched && nameInvalid ? true : undefined}
                aria-describedby={
                  nameTouched && nameInvalid ? `${nameFieldId}-error` : undefined
                }
                onBlur={() => setNameTouched(true)}
                onChange={(event) => {
                  setNameTouched(true);
                  setSelectedProduct({
                    ...selectedProduct,
                    name: event.target.value,
                  });
                }}
              />
              {nameTouched && nameInvalid ? (
                <p id={`${nameFieldId}-error`} className="text-sm text-destructive">
                  {t("settingsPanel.productManagementPanel.nameMustBeSet")}
                </p>
              ) : null}
            </div>

            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor={summaryFieldId}>
                {t("settingsPanel.productManagementPanel.productSummary")}
              </Label>
              <Textarea
                id={summaryFieldId}
                rows={3}
                value={selectedProduct.productSummary || ""}
                disabled={productReadOnly}
                onChange={(event) =>
                  setSelectedProduct({
                    ...selectedProduct,
                    productSummary: event.target.value,
                  })
                }
              />
            </div>

            <div
              role="group"
              aria-labelledby={schemaCategoryLabelId}
              className="flex min-w-0 flex-col gap-1.5"
            >
              <span
                id={schemaCategoryLabelId}
                className="text-sm font-medium leading-none"
              >
                {t("settingsPanel.productManagementPanel.schemaCategory")}
              </span>
              <DIN276CategoryPicker
                className="!w-full [&>button]:!w-full"
                value={selectedProduct.schemaCategory}
                disabled={productReadOnly}
                placeholder={t(
                  "settingsPanel.productManagementPanel.schemaCategoryFilterPlaceholder",
                )}
                onChange={(code) =>
                  setSelectedProduct({
                    ...selectedProduct,
                    schema: "DIN276",
                    schemaCategory: code,
                  })
                }
              />
            </div>

            {selectedProduct.schemaCategory ||
            Object.keys(selectedProduct.properties || {}).length > 0 ? (
              <fieldset
                disabled={productReadOnly}
                className="m-0 min-w-0 border-0 p-0"
              >
                <DinPropertySheet
                  typeCode={selectedProduct.schemaCategory}
                  properties={selectedProduct.properties || {}}
                  onChange={(p: any) =>
                    setSelectedProduct({
                      ...selectedProduct,
                      properties: p,
                    })
                  }
                  showExtras
                  readOnly={productReadOnly}
                  additionalFields={PRODUCT_DIMENSION_FIELDS}
                />
              </fieldset>
            ) : null}
          </div>
        </TabsContent>

        {/* Telemetrie */}
        {!productReadOnly ? (
          <TabsContent value="telemetry">
            <div className="flex min-w-0 max-w-2xl flex-col gap-4">
              <Section
                title={t("settingsPanel.productManagementPanel.telemetryFields")}
              >
                {telemetryFields.length === 0 ? (
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-semibold text-foreground">
                      {t(
                        "settingsPanel.productManagementPanel.telemetryFieldsEmpty",
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        "settingsPanel.productManagementPanel.telemetryFieldsHint",
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="flex min-w-0 flex-col gap-3">
                    {telemetryFields.map(
                      (field: TelemetryField, index: number) => {
                        const rowNameId = `${telemetryFieldIdBase}-name-${index}`;
                        const rowTypeId = `${telemetryFieldIdBase}-type-${index}`;
                        const rowInvalid =
                          !field.name || field.name.trim().length === 0;

                        return (
                          <div
                            key={`telemetry-field-${index}`}
                            className="flex min-w-0 flex-wrap items-end gap-3"
                          >
                            <div className="flex min-w-[180px] flex-1 flex-col gap-1.5">
                              <Label htmlFor={rowNameId}>
                                {t("settingsPanel.productManagementPanel.name")}
                                <span aria-hidden className="ml-0.5 text-destructive">
                                  *
                                </span>
                              </Label>
                              <Input
                                id={rowNameId}
                                required
                                value={field.name ?? ""}
                                aria-invalid={rowInvalid ? true : undefined}
                                aria-describedby={
                                  rowInvalid ? `${rowNameId}-error` : undefined
                                }
                                onChange={(event) => {
                                  const newTelemetryFields = [...telemetryFields];

                                  newTelemetryFields[index] = new TelemetryField(
                                    event.target.value,
                                    field.type,
                                  );

                                  setTelemetryFields(newTelemetryFields);
                                }}
                              />
                              {rowInvalid ? (
                                <p
                                  id={`${rowNameId}-error`}
                                  className="text-sm text-destructive"
                                >
                                  {t(
                                    "settingsPanel.productManagementPanel.nameMustBeSet",
                                  )}
                                </p>
                              ) : null}
                            </div>
                            <div className="flex w-[200px] min-w-0 flex-col gap-1.5">
                              <Label htmlFor={rowTypeId}>
                                {t("settingsPanel.productManagementPanel.type")}
                              </Label>
                              <Select
                                value={field.type}
                                onValueChange={(value) => {
                                  const newTelemetryFields = [...telemetryFields];

                                  newTelemetryFields[index] = new TelemetryField(
                                    field.name,
                                    value as FieldType,
                                  );

                                  setTelemetryFields(newTelemetryFields);
                                }}
                              >
                                <SelectTrigger id={rowTypeId}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {fieldType.map((option) => (
                                    <SelectItem key={option.key} value={option.key}>
                                      {option.text}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={t(
                                "settingsPanel.productManagementPanel.removeTelemetryField",
                              )}
                              title={t(
                                "settingsPanel.productManagementPanel.removeTelemetryField",
                              )}
                              onClick={() => {
                                setTelemetryFields(
                                  telemetryFields.filter(
                                    (entry: TelemetryField) => field !== entry,
                                  ),
                                );
                              }}
                            >
                              <Trash2 aria-hidden className="h-4 w-4" />
                            </Button>
                          </div>
                        );
                      },
                    )}
                  </div>
                )}
                <div className="mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    data-guide-id="settings.productManagement.addTelemetryField"
                    className="gap-2"
                    disabled={!fieldsComplete()}
                    onClick={() => {
                      setTelemetryFields([
                        ...telemetryFields,
                        new TelemetryField(),
                      ]);
                    }}
                  >
                    <Plus aria-hidden className="h-4 w-4" />
                    {t("settingsPanel.productManagementPanel.addTelemetryField")}
                  </Button>
                </div>
              </Section>
            </div>
          </TabsContent>
        ) : null}

        {/* Betriebszustand */}
        {!productReadOnly ? (
          <TabsContent value="operatingState">
            <div className="flex min-w-0 flex-col gap-4">
              <Section title={t("settingsPanel.productManagementPanel.normal")}>
                <OperatingStateRow
                  swatchClassName="bg-success"
                  description={t(
                    "settingsPanel.productManagementPanel.deviceWorksNormally",
                  )}
                >
                  {t(
                    "settingsPanel.productManagementPanel.standardOperatingState",
                  )}
                </OperatingStateRow>
              </Section>

              <Section title={t("settingsPanel.productManagementPanel.warning")}>
                <OperatingStateRow
                  swatchClassName="bg-warning"
                  description={t(
                    "settingsPanel.productManagementPanel.deviceWorksWithRestrictions",
                  )}
                >
                  <div className="flex min-w-0 flex-col gap-2">
                    {renderAddConditionMenu((condition: Condition) => {
                      setWarningConditions([...warningConditions, condition]);
                    })}
                    {warningConditions.map(
                      (condition: Condition, index: number) => (
                        <div
                          key={`condition${index}`}
                          className="flex min-w-0 items-start gap-2"
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={t(
                              "settingsPanel.productManagementPanel.removeCondition",
                            )}
                            title={t(
                              "settingsPanel.productManagementPanel.removeCondition",
                            )}
                            onClick={() => {
                              setWarningConditions(
                                warningConditions.filter(
                                  (entry: Condition) => condition !== entry,
                                ),
                              );
                            }}
                          >
                            <Trash2 aria-hidden className="h-4 w-4" />
                          </Button>
                          <div className="min-w-0 flex-1">
                            <ConditionSection
                              condition={condition}
                              setCondition={(condition: Condition) => {
                                const newConditions = [...warningConditions];

                                newConditions[index] = condition;

                                setWarningConditions(newConditions);
                              }}
                            ></ConditionSection>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </OperatingStateRow>
              </Section>

              <Section title={t("settingsPanel.productManagementPanel.critical")}>
                <OperatingStateRow
                  swatchClassName="bg-destructive"
                  description={t(
                    "settingsPanel.productManagementPanel.deviceOperationDisturbed",
                  )}
                >
                  <div className="flex min-w-0 flex-col gap-2">
                    {renderAddConditionMenu((condition: Condition) => {
                      setCriticalConditions([...criticalConditions, condition]);
                    })}
                    {criticalConditions.map(
                      (condition: Condition, index: number) => (
                        <div
                          key={`condition${index}`}
                          className="flex min-w-0 items-start gap-2"
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={t(
                              "settingsPanel.productManagementPanel.removeCondition",
                            )}
                            title={t(
                              "settingsPanel.productManagementPanel.removeCondition",
                            )}
                            onClick={() => {
                              setCriticalConditions(
                                criticalConditions.filter(
                                  (entry: Condition) => condition !== entry,
                                ),
                              );
                            }}
                          >
                            <Trash2 aria-hidden className="h-4 w-4" />
                          </Button>
                          <div className="min-w-0 flex-1">
                            <ConditionSection
                              condition={condition}
                              setCondition={(condition: Condition) => {
                                const newConditions = [...criticalConditions];

                                newConditions[index] = condition;

                                setCriticalConditions(newConditions);
                              }}
                            ></ConditionSection>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </OperatingStateRow>
              </Section>

              <Section
                title={t("settingsPanel.productManagementPanel.maintenance")}
              >
                <OperatingStateRow
                  swatchClassName="bg-blue-600"
                  description={t(
                    "settingsPanel.productManagementPanel.deviceUnderMaintenance",
                  )}
                >
                  {t("settingsPanel.productManagementPanel.explicitlySetState")}
                </OperatingStateRow>
              </Section>

              <Section title={t("settingsPanel.productManagementPanel.notInUse")}>
                <OperatingStateRow
                  swatchClassName="bg-purple-600"
                  description={t(
                    "settingsPanel.productManagementPanel.deviceNotInUse",
                  )}
                >
                  {t("settingsPanel.productManagementPanel.explicitlySetState")}
                </OperatingStateRow>
              </Section>
            </div>
          </TabsContent>
        ) : null}

        {/* Geräte */}
        <TabsContent value="devices">
          <div className="min-w-0">
            <AssetList product={selectedProduct}></AssetList>
          </div>
        </TabsContent>

        {/* BEG-EM funding eligibility over time (BAFA lists). Only for saved
            products — the view is keyed on the product id. */}
        {selectedProduct._id ? (
          <TabsContent value="eligibility">
            <div className="min-w-0">
              <ProductEligibilityPanel product={selectedProduct} />
            </div>
          </TabsContent>
        ) : null}

        {/* Dokumente */}
        <TabsContent value="documents">
          <div className="flex min-w-0 flex-col gap-4">
            {!productReadOnly ? (
              <DocumentUploadArea
                onFiles={(files: File[]) =>
                  setBufferedDocs((prev) => [
                    ...prev,
                    ...files.map((f) => ({ file: f, role: "other" })),
                  ])
                }
              />
            ) : null}
            {bufferedDocs.length > 0 ? (
              <div className="flex min-w-0 flex-col gap-2">
                <h3 className="text-sm font-semibold">
                  {t("settingsPanel.productManagementPanel.bufferedDocsLabel")}
                </h3>
                {bufferedDocs.map((entry, idx) => {
                  const roleId = `${bufferedDocIdBase}-role-${idx}`;

                  return (
                    <div
                      key={`${entry.file.name}-${idx}`}
                      className="flex min-w-0 flex-wrap items-end gap-3 rounded-md border p-3"
                    >
                      <div className="min-w-[160px] flex-1 break-words text-sm">
                        {entry.file.name}
                      </div>
                      <div className="flex w-[200px] min-w-0 flex-col gap-1.5">
                        <Label htmlFor={roleId}>
                          {t("settingsPanel.productManagementPanel.documentRole")}
                        </Label>
                        <Select
                          value={entry.role}
                          onValueChange={(value) =>
                            setBufferedDocs((prev) =>
                              prev.map((b, i) =>
                                i === idx ? { ...b, role: value } : b,
                              ),
                            )
                          }
                        >
                          <SelectTrigger id={roleId}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PRODUCT_DOC_ROLES.map((role) => (
                              <SelectItem key={role} value={role}>
                                {t(
                                  `settingsPanel.productManagementPanel.role${role.charAt(0).toUpperCase()}${role.slice(1)}`,
                                )}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        variant="outline"
                        className="gap-2"
                        aria-label={`${t("global.delete")}: ${entry.file.name}`}
                        onClick={() =>
                          setBufferedDocs((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                      >
                        <Trash2 aria-hidden className="h-4 w-4" />
                        {t("global.delete")}
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : null}
            <div className="min-w-0">
              <AssetDocumentsPanel
                hideDropzone
                productDocuments={productDocs.map((d: any) => ({
                  id: String(d._id),
                  supersededBy:
                    d.supersededBy != null ? String(d.supersededBy) : null,
                  type: roleLabel(d),
                  synopsis: docTitle(d.path || ""),
                  url: `/managed-files/${d._id}`,
                  summary: d.summary || "",
                  size: d.size,
                  uploadedAt: d.updated ?? d.createdAt ?? d.uploadedAt,
                }))}
                onOpenProductDocument={async (url: string) => {
                  const id = url.replace(/^\/managed-files\//, "");
                  await openDocument({ _id: id });
                }}
              />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </>
  ) : null;

  // ── Deep-Link-Platzhalter ───────────────────────────────────────────────

  const deepLinkPlaceholder = (
    <div className="flex min-w-0 flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        {t(
          "settingsPanel.productManagementPanel.loadingProduct",
          "Produkt wird geladen …",
        )}
      </p>
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-9 w-full max-w-md" />
      <Skeleton className="h-[240px] w-full" />
    </div>
  );

  // Oberste Ebene: die eigenen Produkte und ihre installierte Basis (Assets bei allen Mandanten).
  const overview = (
    <Tabs
      value={section}
      onValueChange={(value) => {
        setSection(value);
        const url = new URL(window.location.href);
        if (value === "installation") url.searchParams.set("section", value);
        else url.searchParams.delete("section");
        window.history.replaceState(window.history.state, "", url.toString());
      }}
      className="min-w-0"
    >
      <TabsList>
        <TabsTrigger value="products">{t("settingsPanel.productManagementPanel.products")}</TabsTrigger>
        <TabsTrigger value="installation">{t("installedBase.title")}</TabsTrigger>
      </TabsList>
      <TabsContent value="products" className="flex min-w-0 flex-col gap-4">
        {productList}
      </TabsContent>
      <TabsContent value="installation">
        <InstalledBasePanel />
      </TabsContent>
    </Tabs>
  );

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      {selectedProduct
        ? productDetail
        : deepLinkPending
          ? deepLinkPlaceholder
          : overview}
    </div>
  );
}
